// Plan 24a, Task 2.5 — Lobby Joiner screen (Screen 5)
//
// READ-ONLY variant of the Lobby Host screen.
// The joiner can see the room but cannot control it.
//
// Panel (720px wide, padding: 'lg') with:
//   - Top: RoomCodeDisplay (shows the code the joiner entered)
//   - Center (two-column row):
//     Left (~60%): 2-column × 4-row player slot grid (8 cells max)
//       Empty slots: plain empty placeholder (no Add Bot / Add Local Player buttons)
//       Filled slots: colored circle avatar + player name (no kick buttons)
//     Right (~40%): map preview placeholder + map name + Dice row
//       Rendered with opacity: 0.55; pointer-events: none (locked/read-only)
//   - Bottom: "Waiting for host to start..." status text + LEAVE button
//
// Design system references:
//   - docs/design/tokens.json — color.player.*, spacing.sm, color.text.primary,
//     color.text.secondary, typography.family.headline, typography.size.caption
//   - docs/design/components.md — Panel §3, RoomCodeDisplay §15, Button §1

import type { ServerMessage } from '@shared/protocol';
import { deserializeGameState } from '@shared/protocol';
import { COLOR_ID_TO_HEX, type CharacterCustomization } from '@shared/game/types';
import type { ColorId } from '../../renderer/character';
import { createPanel } from '../components/panel';
import { createRoomCodeDisplay } from '../components/room-code';
import { createButton } from '../components/button';
import type { PanelHandle } from '../components/panel';
import type { RoomCodeDisplayHandle } from '../components/room-code';
import type { UIComponentHandle } from '../components/types';
import type { ScreenHandle, ScreenMountContext } from './types';
import type { LobbyMountPayload } from './lobby-host';
import { createCharacterSelectScreen } from './character-select';
import { buildWarmupSubscribeMessage, hasSeenWarmupTutorial } from '../../portal-entry';

// ─────────────────────────────────────────────────────────────────────────────
// Re-export public types needed by other screens
// ─────────────────────────────────────────────────────────────────────────────

export type { LobbyMountPayload };

// ─────────────────────────────────────────────────────────────────────────────
// Internal types
// ─────────────────────────────────────────────────────────────────────────────

interface PlayerEntry {
  playerId: string;
  playerName: string;
  isBot: boolean;
  isLocal: boolean;
  isReady: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants (tokens from docs/design/tokens.json)
// ─────────────────────────────────────────────────────────────────────────────

const MAX_PLAYERS = 8;
const PANEL_WIDTH = '720px';
// No breakpoint tokens currently exist in docs/design/tokens.json.
const BREAKPOINT_WIDE = '1280px';
const BREAKPOINT_NARROW = '720px';

// color.player.* from tokens.json
const PLAYER_COLORS: readonly string[] = [
  '#E04040', // red
  '#4080D0', // blue
  '#40B040', // green
  '#E8D020', // yellow
  '#E08820', // orange
  '#9040C0', // purple
  '#E06090', // pink
  '#40B0C0', // cyan
];

// spacing.sm = 8px (slot gap)
const SLOT_GAP = '8px';
// color.text.primary = #FFFFFF
const COLOR_TEXT_PRIMARY = '#FFFFFF';
// color.text.secondary = #B0B8C8
const COLOR_TEXT_SECONDARY = '#B0B8C8';
const COLOR_SUCCESS = '#5CAD40';
const BORDER_RADIUS_PILL = '9999px';
// typography.family.headline
const FONT_HEADLINE =
  "var(--typography-family-headline, 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif)";
function injectLobbyResponsiveStyles(): void {
  const styleId = 'lobby-responsive-styles';
  if (document.getElementById(styleId)) return;

  const styleEl = document.createElement('style');
  styleEl.id = styleId;
  styleEl.textContent = `
    .screen-lobby-host,
    .screen-lobby-joiner {
      --lobby-screen-padding: 24px;
      --lobby-screen-gap: 24px;
      --lobby-panel-padding-y: 24px;
      --lobby-panel-padding-x: 28px;
      --lobby-panel-content-gap: 20px;
      --lobby-room-code-font-size: 48px;
      --lobby-slot-min-height: 100px;
      --lobby-slot-padding: 12px 8px;
      --lobby-empty-slot-padding: 8px;
      --lobby-slot-gap: 6px;
      --lobby-avatar-size: 40px;
      --lobby-settings-padding: 10px;
      --lobby-settings-gap: 8px;
      --layout-button-min-height: 44px;
      --layout-button-padding: 10px 18px;
      --typography-size-h1: var(--lobby-room-code-font-size);
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--lobby-screen-gap);
      width: 100%;
      height: var(--app-viewport-height, 100%);
      padding: var(--lobby-screen-padding);
      box-sizing: border-box;
      overflow: hidden;
    }

    .lobby-responsive__panel-wrapper {
      flex: 0 0 clamp(280px, 24vw, 360px);
      max-width: 360px;
      min-width: 0;
      z-index: 10;
    }

    .lobby-responsive__panel {
      width: 100%;
      max-width: ${PANEL_WIDTH};
      max-height: calc(var(--app-viewport-height, 100vh) - (var(--lobby-screen-padding) * 2));
      padding: var(--lobby-panel-padding-y) var(--lobby-panel-padding-x) !important;
      overflow-y: auto;
      overscroll-behavior: contain;
    }

    .lobby-responsive__inline-select {
      display: flex;
      flex: 0 0 clamp(260px, 23vw, 340px);
      min-width: 0;
      height: 100%;
      min-height: 0;
      z-index: 100;
      background: transparent;
      pointer-events: auto;
      align-items: center;
      position: relative;
    }

    .lobby-responsive__inline-select-close {
      position: absolute;
      top: 8px;
      right: 8px;
      z-index: 4;
      width: 48px !important;
      height: 48px !important;
      min-width: 48px !important;
      min-height: 48px !important;
      max-width: 48px !important;
      max-height: 48px !important;
      padding: 0 !important;
    }

    @media (min-width: ${BREAKPOINT_WIDE}) {
      .lobby-responsive__panel-wrapper {
        flex-basis: 360px;
      }
    }

    @media (min-width: ${BREAKPOINT_NARROW}) and (max-width: 1279px) {
      .screen-lobby-host,
      .screen-lobby-joiner {
        --lobby-screen-gap: 16px;
      }

      .lobby-responsive__panel-wrapper {
        flex-basis: clamp(260px, 30vw, 340px);
      }

      .lobby-responsive__inline-select {
        flex-basis: clamp(240px, 30vw, 320px);
      }
    }

    @media (max-width: 719px) {
      .screen-lobby-host,
      .screen-lobby-joiner {
        --lobby-screen-padding: 16px;
        --lobby-screen-gap: 16px;
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        overflow-x: hidden;
        overflow-y: auto;
        padding-bottom: max(16px, env(safe-area-inset-bottom));
      }

      .lobby-responsive__panel-wrapper {
        flex: 0 0 auto;
        width: 100%;
        max-width: ${PANEL_WIDTH};
        align-self: center;
      }

      .lobby-responsive__panel {
        max-height: none;
      }

      .lobby-responsive__inline-select {
        flex: 0 0 auto;
        width: 100%;
        height: auto;
        min-height: 0;
        max-width: ${PANEL_WIDTH};
        align-self: center;
        align-items: stretch;
      }
    }

    @media (max-height: 900px) and (min-width: ${BREAKPOINT_NARROW}) {
      .screen-lobby-host,
      .screen-lobby-joiner {
        --lobby-screen-padding: 16px;
        --lobby-screen-gap: 16px;
        --lobby-panel-padding-y: 14px;
        --lobby-panel-padding-x: 18px;
        --lobby-panel-content-gap: 12px;
        --lobby-room-code-font-size: 40px;
        --lobby-slot-min-height: 78px;
        --lobby-slot-padding: 8px 6px;
        --lobby-empty-slot-padding: 6px;
        --lobby-slot-gap: 4px;
        --lobby-avatar-size: 30px;
        --lobby-settings-padding: 8px;
        --lobby-settings-gap: 6px;
        --layout-button-min-height: 38px;
        --layout-button-padding: 8px 14px;
        --typography-size-h5: 16px;
      }
    }

    @media (max-height: 760px) and (min-width: ${BREAKPOINT_NARROW}) {
      .screen-lobby-host,
      .screen-lobby-joiner {
        --lobby-screen-padding: 10px;
        --lobby-screen-gap: 10px;
        --lobby-panel-padding-y: 10px;
        --lobby-panel-padding-x: 14px;
        --lobby-panel-content-gap: 8px;
        --lobby-room-code-font-size: 34px;
        --lobby-slot-min-height: 64px;
        --lobby-slot-padding: 6px;
        --lobby-empty-slot-padding: 5px;
        --lobby-slot-gap: 3px;
        --lobby-avatar-size: 24px;
        --lobby-settings-padding: 6px;
        --lobby-settings-gap: 4px;
        --layout-button-min-height: 34px;
        --layout-button-padding: 6px 10px;
        --typography-size-h5: 14px;
        --typography-size-caption: 12px;
      }
    }
  `;
  document.head.appendChild(styleEl);
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createLobbyJoinerScreen(ctx: ScreenMountContext<LobbyMountPayload>): ScreenHandle {
  const { gsapRegistry, host, payload, router } = ctx;

  let disposed = false;
  const players: PlayerEntry[] = [];

  // Track per-player customization from state_sync so the always-on panel has initial values.
  const playerCustomizations = new Map<string, CharacterCustomization>();
  // Track the currently open inline character-select overlay dispose function.
  let inlineSelectDispose: (() => void) | null = null;
  let inlineSelectPlayerId: string | null = null;

  // Track button handles for cleanup
  const buttonHandles: UIComponentHandle[] = [];
  const readyButtonHandles: UIComponentHandle[] = [];

  function navigateBack(): void {
    const target = payload.escBackTarget;
    if (target != null) {
      target.beforeNavigate?.();
      router.navigate(target.screenId, target.payload);
      return;
    }
    payload.send({ type: 'leave' });
    payload.send(buildWarmupSubscribeMessage({ isReady: hasSeenWarmupTutorial() }));
    router.navigate('warmup-arena-overlay', { portal: null });
  }

  function recoverAfterMaintenanceRejection(): void {
    navigateBack();
  }

  // ── Root element ─────────────────────────────────────────────────────────

  injectLobbyResponsiveStyles();

  const rootEl = document.createElement('div');
  rootEl.className = 'screen-lobby-joiner';
  rootEl.style.cssText = ['position: relative', 'width: 100%', 'height: 100%'].join('; ');

  // ── Panel wrapper ────────────────────────────────────────────────────────

  const panelWrapper = document.createElement('div');
  panelWrapper.className = 'lobby-responsive__panel-wrapper';
  rootEl.appendChild(panelWrapper);

  // ── Panel ─────────────────────────────────────────────────────────────────

  const panelHandle: PanelHandle = createPanel({
    gsapRegistry,
    mount: panelWrapper,
    padding: 'lg',
  });

  const panelEl = panelHandle.element;
  panelEl.classList.add('lobby-responsive__panel');
  panelEl.style.width = '100%';
  panelEl.style.boxSizing = 'border-box';

  // Panel content container
  const panelContent = document.createElement('div');
  panelContent.style.cssText = [
    'display: flex',
    'flex-direction: column',
    'gap: var(--lobby-panel-content-gap, 20px)',
  ].join('; ');
  panelEl.appendChild(panelContent);

  // ── Room Code Display ─────────────────────────────────────────────────────
  // Joiner already has the code — show it directly (no '----' placeholder)

  const codeHandle: RoomCodeDisplayHandle = createRoomCodeDisplay({
    gsapRegistry,
    mount: panelContent,
    code: payload.code!,
    subtitle: 'Joining room...',
  });

  // ── Slot grid ────────────────────────────────────────────────────────────

  const centerRow = document.createElement('div');
  centerRow.style.cssText = ['display: flex', `gap: 16px`, 'align-items: flex-start'].join('; ');
  panelContent.appendChild(centerRow);

  // ── Slot grid container (left ~60%) ──────────────────────────────────────

  const gridEl = document.createElement('div');
  gridEl.className = 'lobby-joiner__slot-grid';
  gridEl.style.cssText = ['display: flex', 'flex-wrap: wrap', `gap: ${SLOT_GAP}`, 'flex: 6'].join(
    '; ',
  );
  centerRow.appendChild(gridEl);

  // ── READY controls ───────────────────────────────────────────────────────

  const readyControlsEl = document.createElement('div');
  readyControlsEl.className = 'lobby-joiner__ready-controls';
  readyControlsEl.style.cssText = [
    'display: flex',
    'flex-direction: column',
    'gap: 8px',
    'align-items: stretch',
  ].join('; ');
  panelContent.appendChild(readyControlsEl);

  // ── Panel bottom: status text + LEAVE button ──────────────────────────────

  const panelBottom = document.createElement('div');
  panelBottom.style.cssText = [
    'display: flex',
    'flex-direction: column',
    'align-items: center',
    'gap: 12px',
  ].join('; ');
  panelContent.appendChild(panelBottom);

  // "Waiting for host to start..." status text with pulsing dots animation
  const statusEl = document.createElement('div');
  statusEl.className = 'lobby-joiner__status';

  // Inject keyframes for pulsing dots animation (append to document head once)
  const styleId = 'lobby-joiner-pulse-keyframes';
  if (!document.getElementById(styleId)) {
    const styleEl = document.createElement('style');
    styleEl.id = styleId;
    styleEl.textContent = `
      @keyframes lobby-joiner-pulse-dots {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.3; }
      }
      .lobby-joiner__status-dots {
        display: inline-block;
        animation: lobby-joiner-pulse-dots 2s ease-in-out infinite;
      }
    `;
    document.head.appendChild(styleEl);
  }

  const statusText = document.createElement('span');
  statusText.textContent = 'Waiting for host to start';
  statusText.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    `color: ${COLOR_TEXT_SECONDARY}`,
    'font-size: 16px',
    'font-weight: 700',
    'text-align: center',
  ].join('; ');
  statusEl.appendChild(statusText);

  const dotsSpan = document.createElement('span');
  dotsSpan.className = 'lobby-joiner__status-dots';
  dotsSpan.textContent = '...';
  dotsSpan.style.cssText = [
    `color: ${COLOR_TEXT_SECONDARY}`,
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 16px',
    'font-weight: 700',
  ].join('; ');
  statusEl.appendChild(dotsSpan);

  panelBottom.appendChild(statusEl);

  // LEAVE button
  const leaveHandle = createButton({
    gsapRegistry,
    mount: panelBottom,
    variant: 'danger',
    size: 'small',
    label: 'LEAVE',
    disabled: false,
    onClick: () => {
      if (disposed) return;
      navigateBack();
    },
  });
  leaveHandle.element.setAttribute('data-action', 'leave');
  buttonHandles.push(leaveHandle);

  // ─────────────────────────────────────────────────────────────────────────
  // Inline character-select overlay (joiner's own slot only)
  // ─────────────────────────────────────────────────────────────────────────

  function mountInlineCharacterSelect(playerId: string): void {
    if (inlineSelectDispose != null) {
      inlineSelectDispose();
      inlineSelectDispose = null;
      inlineSelectPlayerId = null;
    }
    if (payload.characterAdapter == null) return;
    const player = players.find((p) => p.playerId === playerId);

    const overlayEl = document.createElement('div');
    overlayEl.className = 'lobby-responsive__inline-select';
    rootEl.appendChild(overlayEl);

    const closeHandle = createButton({
      gsapRegistry,
      variant: 'ghost',
      size: 'icon',
      label: '×',
      onClick: () => {
        inlineSelectDispose?.();
        inlineSelectDispose = null;
        inlineSelectPlayerId = null;
      },
    });
    closeHandle.element.classList.add('lobby-responsive__inline-select-close');
    closeHandle.element.setAttribute('aria-label', 'Close character customization');
    overlayEl.appendChild(closeHandle.element);

    const takenColors: ColorId[] = players
      .filter((p) => p.playerId !== playerId)
      .map((p) => {
        const c = playerCustomizations.get(p.playerId);
        return c?.color ?? null;
      })
      .filter((c): c is ColorId => c != null);

    const initial = playerCustomizations.get(playerId) ?? {
      color: 'blue' as const,
      head: 'horse' as const,
      eyes: 'dots' as const,
      cape: 'none' as const,
    };

    const selectHandle = createCharacterSelectScreen({
      gsapRegistry,
      host: overlayEl,
      router,
      payload: {
        adapter: payload.characterAdapter,
        takenColors,
        initial,
        ready: player?.isReady ?? false,
        playerName: player?.playerName,
        roomCode: payload.code,
        mode: 'inline',
        onCustomizeCamera: (view) => payload.onCustomizeCamera?.({ playerId, view }),
        onSelect: (c: CharacterCustomization) => {
          payload.send({ type: 'set_character_customization', playerId, customization: c });
        },
        onReady: (c: CharacterCustomization, isReady: boolean) => {
          payload.send({ type: 'set_character_customization', playerId, customization: c });
          payload.send({ type: 'set_ready', playerId, isReady });
        },
      },
    });

    // Re-enable pointer events on the character-select panel so clicks register
    // (overlay container is pointer-events:none to let the 3D lobby show through)
    selectHandle.element.style.pointerEvents = 'auto';

    inlineSelectDispose = () => {
      closeHandle.dispose();
      selectHandle.dispose();
      if (overlayEl.parentNode) {
        overlayEl.parentNode.removeChild(overlayEl);
      }
      payload.onCustomizeCamera?.(null);
    };
    inlineSelectPlayerId = playerId;
  }

  function syncInlineCharacterSelect(opts?: { remount?: boolean }): void {
    if (payload.characterAdapter == null || selfPlayerId == null) return;
    if (inlineSelectPlayerId === selfPlayerId && opts?.remount !== true) return;
    if (players.some((player) => player.playerId === selfPlayerId)) {
      mountInlineCharacterSelect(selfPlayerId);
    }
  }

  function disposeReadyButtonHandles(): void {
    for (const h of readyButtonHandles) {
      h.dispose();
    }
    readyButtonHandles.length = 0;
  }

  function renderReadyControls(): void {
    disposeReadyButtonHandles();
    while (readyControlsEl.firstChild) {
      readyControlsEl.removeChild(readyControlsEl.firstChild);
    }
    if (selfPlayerId == null) return;
    const player = players.find((p) => p.playerId === selfPlayerId);
    if (player == null || player.isBot) return;

    const readyHandle = createButton({
      gsapRegistry,
      mount: readyControlsEl,
      variant: player.isReady ? 'secondary' : 'primary',
      size: 'large',
      label: player.isReady ? 'UNREADY' : 'READY',
      onClick: () => {
        if (disposed) return;
        const customization = playerCustomizations.get(player.playerId);
        if (customization != null) {
          payload.send({
            type: 'set_character_customization',
            playerId: player.playerId,
            customization,
          });
        }
        payload.send({ type: 'set_ready', playerId: player.playerId, isReady: !player.isReady });
      },
    });
    readyHandle.element.setAttribute('data-action', 'ready');
    readyHandle.element.style.width = '100%';
    readyButtonHandles.push(readyHandle);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Slot rendering
  // ─────────────────────────────────────────────────────────────────────────

  /** Build one filled player slot cell (no kick button — read-only). */
  function buildFilledSlot(player: PlayerEntry, index: number): HTMLElement {
    const slotEl = document.createElement('div');
    slotEl.setAttribute('data-player-slot', player.playerId);
    slotEl.style.cssText = [
      'display: flex',
      'flex-direction: column',
      'align-items: center',
      'justify-content: center',
      'gap: var(--lobby-slot-gap, 6px)',
      'width: calc(50% - 4px)',
      'min-height: var(--lobby-slot-min-height, 100px)',
      'box-sizing: border-box',
      'padding: var(--lobby-slot-padding, 12px 8px)',
      `background: rgba(0,0,0,0.20)`,
      `border-radius: 10px`,
    ].join('; ');

    // Colored circle avatar
    const avatarEl = document.createElement('div');
    const customization = playerCustomizations.get(player.playerId);
    const playerColor =
      customization != null
        ? `#${COLOR_ID_TO_HEX[customization.color].toString(16).padStart(6, '0')}`
        : (PLAYER_COLORS[index % PLAYER_COLORS.length] ?? '#FFFFFF');
    avatarEl.style.cssText = [
      'width: var(--lobby-avatar-size, 40px)',
      'height: var(--lobby-avatar-size, 40px)',
      `background: ${playerColor}`,
      'border-radius: 50%',
      'flex-shrink: 0',
    ].join('; ');
    slotEl.appendChild(avatarEl);

    // Player name
    const nameEl = document.createElement('div');
    nameEl.textContent = player.playerName;
    nameEl.style.cssText = [
      `color: ${COLOR_TEXT_PRIMARY}`,
      `font-family: ${FONT_HEADLINE}`,
      'font-size: 14px',
      'font-weight: 700',
      'text-align: center',
      'max-width: 100%',
      'overflow: hidden',
      'text-overflow: ellipsis',
      'white-space: nowrap',
    ].join('; ');
    slotEl.appendChild(nameEl);

    if (player.isReady || player.isBot) {
      const readyPill = document.createElement('div');
      readyPill.textContent = 'READY ✓';
      readyPill.setAttribute('data-ready-badge', player.playerId);
      readyPill.style.cssText = [
        `background: ${COLOR_SUCCESS}`,
        'color: #FFFFFF',
        'font-size: 10px',
        'font-weight: 700',
        'letter-spacing: 1px',
        `padding: 2px 8px`,
        `border-radius: ${BORDER_RADIUS_PILL}`,
        'line-height: 1.4',
      ].join('; ');
      slotEl.appendChild(readyPill);
    }

    return slotEl;
  }

  /** Build one empty slot cell — plain placeholder, no buttons. */
  function buildEmptySlot(): HTMLElement {
    const slotEl = document.createElement('div');
    slotEl.className = 'lobby-joiner__empty-slot';
    slotEl.style.cssText = [
      'display: flex',
      'flex-direction: column',
      'align-items: center',
      'justify-content: center',
      'width: calc(50% - 4px)',
      'min-height: var(--lobby-slot-min-height, 100px)',
      'box-sizing: border-box',
      'padding: var(--lobby-empty-slot-padding, 8px)',
      `background: rgba(0,0,0,0.10)`,
      `border-radius: 10px`,
      'border: 1px dashed rgba(255,255,255,0.15)',
    ].join('; ');

    // No buttons — joiner cannot add bot or local player

    return slotEl;
  }

  /** Re-render all 8 slot cells from current player state. */
  function renderSlots(): void {
    // Remove existing children from grid
    while (gridEl.firstChild) {
      gridEl.removeChild(gridEl.firstChild);
    }

    const occupiedCount = players.length;

    // Build filled slots for each player
    for (let i = 0; i < occupiedCount; i++) {
      const player = players[i]!;
      const slotEl = buildFilledSlot(player, i);
      gridEl.appendChild(slotEl);
    }

    // Build empty slots for remaining capacity
    for (let i = occupiedCount; i < MAX_PLAYERS; i++) {
      const slotEl = buildEmptySlot();
      gridEl.appendChild(slotEl);
    }

    renderReadyControls();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Server message handler
  // ─────────────────────────────────────────────────────────────────────────

  let selfPlayerId: string | null = payload.restoredSession?.playerId ?? null;
  let gameStartPending = false;

  const unsubscribe = payload.subscribe((msg: ServerMessage) => {
    if (disposed) return;

    if (msg.type === 'room_joined') {
      selfPlayerId = msg.playerId;
      players.length = 0;
      playerCustomizations.clear();
      for (const joinedPlayer of msg.players) {
        players.push({
          playerId: joinedPlayer.id,
          playerName: joinedPlayer.name,
          isBot: joinedPlayer.isBot,
          isLocal: joinedPlayer.isLocal,
          isReady: joinedPlayer.isBot,
        });
        if (joinedPlayer.customization != null) {
          playerCustomizations.set(joinedPlayer.id, joinedPlayer.customization);
        }
      }
      renderSlots();
      syncInlineCharacterSelect({ remount: inlineSelectPlayerId != null });
      return;
    }

    if (msg.type === 'player_joined') {
      players.push({
        playerId: msg.playerId,
        playerName: msg.playerName,
        isBot: msg.isBot,
        isLocal: msg.isLocal,
        isReady: msg.isBot,
      });
      renderSlots();
      syncInlineCharacterSelect({ remount: inlineSelectPlayerId != null });
      return;
    }

    if (msg.type === 'game_started') {
      gameStartPending = true;
      return;
    }

    if (msg.type === 'state_sync') {
      const state = deserializeGameState(msg.state);
      players.length = 0;
      playerCustomizations.clear();
      for (const id of state.turnOrder) {
        const player = state.players.get(id);
        if (!player) continue;
        players.push({
          playerId: id,
          playerName: player.name,
          isBot: player.isBot,
          isLocal: player.isLocal,
          isReady: player.isBot || player.isReady === true,
        });
      }
      for (const [id, player] of state.players.entries()) {
        if (player.customization != null) {
          playerCustomizations.set(id, player.customization);
        }
      }
      renderSlots();
      syncInlineCharacterSelect();
      if (gameStartPending) {
        gameStartPending = false;
        if (selfPlayerId == null) return;
        router.navigate('board-hud', {
          state,
          localPlayerId: selfPlayerId,
          send: payload.send,
          subscribe: payload.subscribe,
        });
        return;
      }
      return;
    }

    if (msg.type === 'player_left') {
      const idx = players.findIndex((p) => p.playerId === msg.playerId);
      if (idx !== -1) {
        players.splice(idx, 1);
        renderSlots();
        if (inlineSelectPlayerId === msg.playerId) {
          inlineSelectDispose?.();
          inlineSelectDispose = null;
          inlineSelectPlayerId = null;
        }
      }
      return;
    }

    // Handle server error notifications (e.g. room_not_found, room_full).
    // The joiner mounted optimistically and sent join_room; if the room doesn't
    // exist or is full the server returns this. Bounce back to the main menu so
    // the user isn't stuck on a permanently-empty "Joining room..." screen.
    if (msg.type === 'notification' && msg.level === 'error') {
      if (msg.text === 'room_not_found' || msg.text === 'room_full') {
        // TODO: surface a toast/error pill before navigating; for now navigate
        // immediately so the user can re-enter a valid code.

        console.warn('[lobby-joiner] join failed:', msg.text);
        navigateBack();
        return;
      }
    }

    if (msg.type === 'notification' && msg.text === 'maintenance_update_pending') {
      recoverAfterMaintenanceRejection();
      return;
    }

    if (msg.type === 'notification' && msg.text === 'color_taken') {
      inlineSelectDispose?.();
      inlineSelectDispose = null;
      inlineSelectPlayerId = null;
      return;
    }

    // settings_changed: no-op — joiner cannot change settings.
    // The read-only panel already reflects default values.
    // A future enhancement could update the locked panel display.
  });

  // ─────────────────────────────────────────────────────────────────────────
  // Mount
  // ─────────────────────────────────────────────────────────────────────────

  // Initial render of empty slots
  renderSlots();

  // Append root to host and trigger panel entrance animation
  host.appendChild(rootEl);
  panelHandle.show();

  // ─────────────────────────────────────────────────────────────────────────
  // Send join_room immediately on mount
  // ─────────────────────────────────────────────────────────────────────────

  if (payload.skipInitialRequest !== true) {
    payload.send({
      type: 'join_room',
      code: payload.code!,
      playerName: payload.playerName,
      ...(payload.preferredCustomization === undefined
        ? {}
        : { customization: payload.preferredCustomization }),
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // dispose
  // ─────────────────────────────────────────────────────────────────────────

  function disposeButtonHandles(): void {
    for (const h of buttonHandles) {
      h.dispose();
    }
    buttonHandles.length = 0;
    disposeReadyButtonHandles();
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Close any open inline character-select overlay
    if (inlineSelectDispose != null) {
      inlineSelectDispose();
      inlineSelectDispose = null;
    }

    unsubscribe();
    disposeButtonHandles();
    codeHandle.dispose();
    panelHandle.dispose();

    if (rootEl.parentNode) {
      rootEl.parentNode.removeChild(rootEl);
    }
  }

  return {
    element: rootEl,
    dispose,
  };
}
